/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BarListResponse } from '../models/BarListResponse';
import type { DataHealthResponse } from '../models/DataHealthResponse';
import type { InstrumentKind } from '../models/InstrumentKind';
import type { InstrumentListResponse } from '../models/InstrumentListResponse';
import type { InstrumentResponse } from '../models/InstrumentResponse';
import type { InstrumentStatus } from '../models/InstrumentStatus';
import type { LatestBarsResponse } from '../models/LatestBarsResponse';
import type { Timeframe } from '../models/Timeframe';
import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';
export class MarketDataService {
    constructor(public readonly httpRequest: BaseHttpRequest) {}
    /**
     * List Bars
     * OHLCV for one series, ascending, half-open on `[from, to)`.
     *
     * `instrument_id` and `timeframe` are required rather than optional filters. A bars
     * query without them is a scan of the whole hypertable, and "OHLCV for everything" is
     * not a question with a useful answer.
     *
     * Half-open, matching `read_bars`, `find_gaps` and `aggregate`: one window's `to` is the
     * next window's `from` and the boundary bar is returned exactly once. A client stitching
     * pages by time rather than by cursor gets the same guarantee.
     * @returns BarListResponse Successful Response
     * @throws ApiError
     */
    public listBars({
        instrumentId,
        timeframe,
        from,
        to,
        cursor,
        limit = 500,
    }: {
        instrumentId: number,
        timeframe: Timeframe,
        from?: (string | null),
        to?: (string | null),
        cursor?: (string | null),
        limit?: number,
    }): CancelablePromise<BarListResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/v1/bars',
            query: {
                'instrument_id': instrumentId,
                'timeframe': timeframe,
                'from': from,
                'to': to,
                'cursor': cursor,
                'limit': limit,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Latest Bars
     * The newest stored bar for each of several instruments. One query, not N.
     *
     * Note what this is *not*: the current price. It is the newest bar this platform has
     * stored, which on a stale series is an hour old. `/data-health` is where a caller finds
     * out which of these to believe, and that separation is deliberate — a single endpoint
     * returning a price and a freshness flag invites reading the price and ignoring the flag.
     * @returns LatestBarsResponse Successful Response
     * @throws ApiError
     */
    public latestBars({
        timeframe,
        instrumentIds,
    }: {
        timeframe: Timeframe,
        instrumentIds: Array<number>,
    }): CancelablePromise<LatestBarsResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/v1/bars/latest',
            query: {
                'timeframe': timeframe,
                'instrument_ids': instrumentIds,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Data Health
     * Per instrument and timeframe: last bar, gaps, staleness. The S11 matrix.
     *
     * One row per series that has bars. There is no table of series the platform *intends*
     * to hold — universes are not built — so this reports what exists rather than measuring
     * it against an expectation that is not written down anywhere. A symbol the operator
     * believes is being ingested and which is absent here is the finding, and its absence is
     * the report.
     *
     * The freshness column is `gates.freshness`, which delegates its stale bound to the same
     * `is_stale` the engine halts on. A screen that said `fresh` for a series the engine is
     * refusing to evaluate would be worse than no screen at all.
     * @returns DataHealthResponse Successful Response
     * @throws ApiError
     */
    public dataHealth({
        instrumentId,
    }: {
        instrumentId?: (Array<number> | null),
    }): CancelablePromise<DataHealthResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/v1/data-health',
            query: {
                'instrument_id': instrumentId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * List Instruments
     * One page of instruments, ordered by symbol.
     *
     * `status` is not in the spec's parameter list and is here because the spec's own
     * `is_tradeable` distinction is the filter the UI needs first: 1,335 rows including
     * delisted pairs is a list nobody scrolls.
     * @returns InstrumentListResponse Successful Response
     * @throws ApiError
     */
    public listInstruments({
        exchange,
        kind,
        status,
        search,
        cursor,
        limit = 100,
    }: {
        exchange?: (string | null),
        kind?: (InstrumentKind | null),
        status?: (InstrumentStatus | null),
        search?: (string | null),
        cursor?: (string | null),
        limit?: number,
    }): CancelablePromise<InstrumentListResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/v1/instruments',
            query: {
                'exchange': exchange,
                'kind': kind,
                'status': status,
                'search': search,
                'cursor': cursor,
                'limit': limit,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Instrument
     * @returns InstrumentResponse Successful Response
     * @throws ApiError
     */
    public getInstrument({
        instrumentId,
    }: {
        instrumentId: number,
    }): CancelablePromise<InstrumentResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/v1/instruments/{instrument_id}',
            path: {
                'instrument_id': instrumentId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
}
